import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useLocation } from 'react-router';
import { ruRU } from '@mui/x-data-grid-pro/locales';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { CustomNoRowsOverlay } from '../../../../components/data-grid/utils';
import { normalizeDataGridModel } from '../../../../utils/normalizeDataGridModel';
import { TABLE_COLLECTION_COLUMNS } from '../../constants';
import { getCollectionListAction, setPlanningOpenAction } from '../../actions';
import { getCollectionListSelector, getIsCollectionListLoadingSelector } from '../../selectors';

const CollectionWrapper = ({
	row,
	onGetCollectionList,
	collectionList,
	isCollectionListLoading,
	modes,
	onSetPlanningOpen,
}) => {
	const { search } = useLocation();

	const query = new URLSearchParams(search);

	useEffect(() => {
		onGetCollectionList({ row, searchQuery: query.toString() });
		onSetPlanningOpen(row);
	}, [row.id]);

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('childrenSorts', jsonSorts);

		onGetCollectionList({ row, searchQuery: query.toString() });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			let jsonFilters = [];

			jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLLECTION_COLUMNS(modes)));

			query.set('childrenFilters', jsonFilters);

			onGetCollectionList({ row, searchQuery: query.toString() });
		}
	});

	const defaultColumnVisibility = {
		address_from: false,
		drop_at: false,
		pickup_at: false,
		cost: false,
		comment: false,
	};

	const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultColumnVisibility);

	const [columnModel, setColumnModel] = useState({});

	const handleColumnWidthChange = params => {
		setColumnModel(prev => ({
			...prev,
			[params.colDef.field]: params.width,
		}));
	};

	const columnsWithSavedWidth = TABLE_COLLECTION_COLUMNS(modes).map(col => ({
		...col,
		width: columnModel[col.field] || col.width,
		flex: columnModel[col.field] ? 0 : col.flex,
	}));

	return (
		<>
			<DataGridPro
				key={`${row.id}`}
				slots={{
					noRowsOverlay: CustomNoRowsOverlay,
				}}
				slotProps={{
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
				}}
				initialState={{
					pinnedColumns: { left: ['empty', '__check__', 'id', 'actions', '__detail_panel_toggle__'] },
				}}
				rows={collectionList ?? []}
				columns={columnsWithSavedWidth}
				onColumnWidthChange={handleColumnWidthChange}
				hideFooterPagination
				hideFooter
				rowHeight={40}
				columnHeaderHeight={40}
				loading={Boolean(isCollectionListLoading.find(el => el === row.id))}
				localeText={ruRU.components.MuiDataGrid.defaultProps.localeText}
				className="no-border"
				sortingMode="server"
				onSortModelChange={handleSortChange}
				filterMode="server"
				filterDebounceMs={300}
				onFilterModelChange={handleFilterChange}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
				disableMultipleColumnsSorting
				disableRowSelectionOnClick
			/>
		</>
	);
};

const mapStateToProps = createStructuredSelector({
	collectionList: getCollectionListSelector(),
	isCollectionListLoading: getIsCollectionListLoadingSelector(),
});

const mapDispatchToProps = {
	onGetCollectionList: getCollectionListAction,
	onSetPlanningOpen: setPlanningOpenAction,
};

export const CollectionComponent = connect(mapStateToProps, mapDispatchToProps)(CollectionWrapper);

CollectionWrapper.propTypes = {
	row: PropTypes.object.isRequired,
	onGetCollectionList: PropTypes.func,
	collectionList: PropTypes.array,
	isCollectionListLoading: PropTypes.array.isRequired,
	modes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	onSetPlanningOpen: PropTypes.func.isRequired,
};
